// Example2SupportProblem.js - Based on GCodeGenerator with overhang analysis
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { createCachedMaterialsLaserCutInterlocking } from "../utils/materialManager";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import { sliceModelXZ } from "../utils/sliceModel.js";
import { generateInfillLines } from "../utils/generateInfillLines";
import { createInfillLines } from "../utils/visualizeInfill";
import { useCameraSync } from "../contexts/CameraContext";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import { CalcDraftAngles } from '../utils/geometryProcessing/CalcDraftAngles';
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./Example2SupportProblem.css";

const Example2SupportProblem = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const { createVisualization, removeVisualHelpers } = useVisualHelpers();
  const helpersGroupRef = useRef(null);
  const gridHelperRef = useRef(null);
  const { updateCameraState } = useCameraSync();

  // 保留GCodeGenerator的完整状态
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [layerHeight, setLayerHeight] = useState(0.2);
  const [infillDensity, setInfillDensity] = useState(20);
  const [nozzleDiameter, setNozzleDiameter] = useState(0.4);
  const [processingStatus, setProcessingStatus] = useState("");
  const [slicingProgress, setSlicingProgress] = useState(0);
  const [previewSlices, setPreviewSlices] = useState([]);
  const [currentLayer, setCurrentLayer] = useState(0);

  // 新增：Example2交互状态
  const [currentStep, setCurrentStep] = useState(1);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [driverObj, setDriverObj] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [analysisComplete, setAnalysisComplete] = useState(false);

  // 新增：悬垂分析相关refs
  const overhangMarkersRef = useRef([]);
  const analysisMeshRef = useRef(null);
  const [raycaster] = useState(() => new THREE.Raycaster());
  const pointer = useRef(new THREE.Vector2());

  const objRef = useRef(null);

  const pageId = useMemo(() => `support-problem-example`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  // 相机同步
  useEffect(() => {
    if (!cameraRef.current || !controlsRef.current) return;
    
    let animationId;
    let lastUpdateTime = 0;
    const updateInterval = 50;
    
    const updateLoop = (currentTime) => {
      if (currentTime - lastUpdateTime >= updateInterval) {
        if (cameraRef.current && controlsRef.current) {
          updateCameraState(pageId, cameraRef.current, controlsRef.current);
        }
        lastUpdateTime = currentTime;
      }
      animationId = requestAnimationFrame(updateLoop);
    };
    
    animationId = requestAnimationFrame(updateLoop);
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [pageId, updateCameraState]);

  // 初始化driver.js
  useEffect(() => {
    const newDriverObj = driver({
      popoverClass: "cameleon-tutorial-popover",
      stagePadding: 4,
    });
    setDriverObj(newDriverObj);

    return () => {
      if (newDriverObj) {
        newDriverObj.destroy();
      }
    };
  }, []);

  const cachedMaterials = useMemo(
    () => createCachedMaterialsLaserCutInterlocking(),
    []
  );

  const infillSpacing = nozzleDiameter * (100 / infillDensity);

  // 保留原始的initHelpers
  const initHelpers = useCallback(() => {
    if (!sceneRef.current) return;

    if (!gridHelperRef.current) {
      const gridHelper = new THREE.GridHelper(200, 20, 0x888888, 0xaaaaaa);
      gridHelper.position.y = -0.5;
      gridHelper.material.opacity = 0.5;
      gridHelper.material.transparent = true;
      sceneRef.current.add(gridHelper);
      gridHelperRef.current = gridHelper;

      const axesHelper = new THREE.AxesHelper(100);
      sceneRef.current.add(axesHelper);
    }
  }, []);

  // 保留原始的clearPreviousObjects
  const clearPreviousObjects = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (gridHelperRef.current) {
      scene.remove(gridHelperRef.current);
      gridHelperRef.current = null;
    }

    if (helpersGroupRef.current) {
      removeVisualHelpers(scene);
      helpersGroupRef.current = null;
    }

    previewSlices.forEach((slice) => {
      scene.remove(slice);
      if (slice.geometry) slice.geometry.dispose();
      if (slice.material) {
        if (Array.isArray(slice.material)) {
          slice.material.forEach((m) => m.dispose());
        } else {
          slice.material.dispose();
        }
      }
    });
    setPreviewSlices([]);

    if (objRef.current) {
      objRef.current.visible = false;
    }
  }, [previewSlices, removeVisualHelpers]);

  // 保留原始的createHelpers
  const createHelpers = useCallback(() => {
    if (!sceneRef.current || !objRef.current) return;

    if (helpersGroupRef.current) {
      removeVisualHelpers(sceneRef.current);
      helpersGroupRef.current = null;
    }

    const boundingBox = new THREE.Box3().setFromObject(objRef.current);
    helpersGroupRef.current = createVisualization(
      objRef.current,
      sceneRef.current,
      boundingBox
    );
  }, [createVisualization, removeVisualHelpers]);

  // 保留完整的sliceModel函数（来自GCodeGenerator）
  const sliceModel = useCallback(() => {
    if (!objRef.current || !sceneRef.current) return;

    setProcessingStatus("Slicing model...");
    setSlicingProgress(0);

    clearPreviousObjects();

    const obj = objRef.current;
    const scene = sceneRef.current;

    obj.visible = false;

    const boundingBox = new THREE.Box3().setFromObject(obj);
    const size = boundingBox.getSize(new THREE.Vector3());

    const spacing = nozzleDiameter * (100 / infillDensity);

    const totalLayers = Math.ceil(size.y / layerHeight);
    const newSlices = [];

    const createSlices = async (layerIndex = 0) => {
      if (layerIndex >= totalLayers) {
        setProcessingStatus("");
        setSlicingProgress(100);
        // 切片完成后，自动进入下一步
        setTimeout(() => {
          setCurrentStep(2);
        }, 1000);
        return;
      }

      setSlicingProgress(Math.floor((layerIndex / totalLayers) * 100));

      const sliceHeight = boundingBox.min.y + layerIndex * layerHeight;

      const result = await sliceModelXZ(obj, scene, sliceHeight);
      if (result && result.outlines) {
        result.outlines.forEach((line) => {
          scene.add(line);
          newSlices.push(line);
        });
      }

      if (result.contourPoints.length > 0) {
        for (const polyline of result.contourPoints) {
          if (polyline.length < 3) continue;

          const polygon2D = polyline.map(([x, , z]) => [x, z]);
          const infillSegments = generateInfillLines(polygon2D, spacing);

          const infillLines = createInfillLines(infillSegments, sliceHeight);
          scene.add(infillLines);
          newSlices.push(infillLines);
        }
      }

      setTimeout(() => {
        createSlices(layerIndex + 1);
      }, 50);
    };

    createSlices(0);
    setPreviewSlices(newSlices);
    setCurrentLayer(0);
  }, [
    clearPreviousObjects,
    layerHeight,
    infillDensity,
    nozzleDiameter
  ]);

  // 保留showLayer函数
  const showLayer = useCallback(
    (layerIndex) => {
      if (previewSlices.length === 0) return;

      const totalLayers = previewSlices.length;
      if (layerIndex < 0) layerIndex = 0;
      if (layerIndex >= totalLayers) layerIndex = totalLayers - 1;

      previewSlices.forEach((slice, index) => {
        slice.visible = index <= layerIndex;
      });

      setCurrentLayer(layerIndex);
    },
    [previewSlices]
  );

  // ===== 新增：悬垂分析功能 =====

  // Draft Angle分析模型 - 3D打印版本
  const analyzeForSupport = useCallback((mesh) => {
    if (!mesh || !mesh.geometry || !sceneRef.current) {
      return;
    }

    try {
      // 清除旧的分析模型
      if (analysisMeshRef.current) {
        sceneRef.current.remove(analysisMeshRef.current);
        if (analysisMeshRef.current.geometry) analysisMeshRef.current.geometry.dispose();
        if (analysisMeshRef.current.material) analysisMeshRef.current.material.dispose();
        analysisMeshRef.current = null;
      }

      // 创建用于draft angle分析的模型副本
      const analysisGeometry = mesh.geometry.clone();
      const analysisMesh = new THREE.Mesh(analysisGeometry, mesh.material.clone());
      
      // 复制原始模型的变换
      analysisMesh.position.copy(mesh.position);
      analysisMesh.rotation.copy(mesh.rotation);
      analysisMesh.scale.copy(mesh.scale);
      analysisMesh.updateMatrixWorld(true);

      // 3D打印的打印方向是垂直向上 (0, 0, 1)
      const printDirection = new THREE.Vector3(0, 1, 0);
      
      // 使用CalcDraftAngles分析整个模型
      CalcDraftAngles.analyze(analysisMesh, printDirection);

      // 将分析模型放置在原模型右侧
      // 自动根据模型尺寸决定偏移距离
const boundingBox = new THREE.Box3().setFromObject(mesh);
const size = new THREE.Vector3();
boundingBox.getSize(size);

// 取模型宽度（x方向），偏移距离设为 1.5 倍宽度
const offsetX = size.x * 1.5;
analysisMesh.position.x += offsetX;

      
      analysisMeshRef.current = analysisMesh;
      sceneRef.current.add(analysisMesh);

      // 创建颜色图例
      // 创建颜色图例（简化版）
const legend = document.createElement("div");
legend.className = "overhang-info";
legend.id = "support-color-scale";
legend.innerHTML = `
  <h4>Overhang Risk</h4>
  <div class="legend-item">
    <span class="legend-color high"></span> High Risk (needs support)
  </div>
  <div class="legend-item">
    <span class="legend-color medium"></span> Medium Risk
  </div>
  <div class="legend-item">
    <span class="legend-color low"></span> Low / Safe
  </div>
`;
document.body.appendChild(legend);


    } catch (err) {
      console.error("Error analyzing support angles:", err);
    }
  }, []);

  const getFaceCenter = useCallback((positions, faceIndex) => {
    const center = new THREE.Vector3();
    for (let i = 0; i < 3; i++) {
      const vertexIndex = faceIndex + i;
      center.x += positions.getX(vertexIndex);
      center.y += positions.getY(vertexIndex);
      center.z += positions.getZ(vertexIndex);
    }
    center.divideScalar(3);
    return center;
  }, []);

  const analyzeOverhangs = useCallback((mesh) => {
    if (!mesh || !mesh.geometry) return [];

    const geometry = mesh.geometry;
    const positions = geometry.attributes.position;
    const normals = geometry.attributes.normal;
    
    if (!normals) {
      geometry.computeVertexNormals();
    }

    const overhangFaces = [];
    const printDirection = new THREE.Vector3(0, 1, 0);

    for (let i = 0; i < positions.count; i += 3) {
      const normal = new THREE.Vector3();
      for (let j = 0; j < 3; j++) {
        const vertexIndex = i + j;
        const vertexNormal = new THREE.Vector3(
          normals.getX(vertexIndex),
          normals.getY(vertexIndex),
          normals.getZ(vertexIndex)
        );
        normal.add(vertexNormal);
      }
      normal.normalize();

      const angle = normal.angleTo(printDirection);
      const degree = THREE.MathUtils.radToDeg(angle);

      if (degree > 45 && degree < 135 && normal.z < 0) {
        const faceCenter = getFaceCenter(positions, i);
        faceCenter.applyMatrix4(mesh.matrixWorld);

        let severity = 'low';
        if (degree > 60 && degree < 120) severity = 'medium';
        if (degree > 75 && degree < 105) severity = 'high';

        overhangFaces.push({
          index: i,
          angle: degree,
          position: faceCenter,
          severity: severity,
          isCorrect: severity === 'high'
        });
      }
    }

    overhangFaces.sort((a, b) => {
      const severityOrder = { high: 3, medium: 2, low: 1 };
      return severityOrder[b.severity] - severityOrder[a.severity];
    });

    return overhangFaces.slice(0, 8);
  }, [getFaceCenter]);

  const createOverhangMarkers = useCallback((overhangs) => {
    if (!sceneRef.current || overhangs.length === 0) return [];

    const markers = [];

    overhangs.forEach((overhang) => {
      const geometry = new THREE.SphereGeometry(1.5, 16, 16);
      let color = 0x00ff00;
      
      if (overhang.severity === 'medium') color = 0xff8800;
      if (overhang.severity === 'high') color = 0xff0000;

      const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.8,
        emissive: color,
        emissiveIntensity: 0.2
      });

      const marker = new THREE.Mesh(geometry, material);
      marker.position.copy(overhang.position);
      marker.userData.isOverhang = true;
      marker.userData.severity = overhang.severity;
      marker.userData.angle = overhang.angle.toFixed(1);
      marker.userData.isCorrect = overhang.isCorrect;

      markers.push(marker);
      sceneRef.current.add(marker);
    });

    overhangMarkersRef.current = markers;
    return markers;
  }, []);

  const clearOverhangMarkers = useCallback(() => {
    overhangMarkersRef.current.forEach(marker => {
      if (sceneRef.current) {
        sceneRef.current.remove(marker);
      }
      if (marker.geometry) marker.geometry.dispose();
      if (marker.material) marker.material.dispose();
    });
    overhangMarkersRef.current = [];

    // 清理draft angle分析模型
    if (analysisMeshRef.current) {
      if (sceneRef.current) sceneRef.current.remove(analysisMeshRef.current);
      if (analysisMeshRef.current.geometry) analysisMeshRef.current.geometry.dispose();
      if (analysisMeshRef.current.material) analysisMeshRef.current.material.dispose();
      analysisMeshRef.current = null;
    }

    // 清除颜色图例
    const colorScale = document.getElementById("support-color-scale");
    if (colorScale) {
      document.body.removeChild(colorScale);
    }
  }, []);

  const analyzeAndShowMarkers = useCallback(() => {
    if (!objRef.current) return;

    setProcessingStatus("Analyzing overhangs and support requirements...");
    
    setTimeout(() => {
      // 1. 分析悬垂区域并创建标记
      const overhangs = analyzeOverhangs(objRef.current);
      createOverhangMarkers(overhangs);
      
      // 2. 进行Draft Angle分析并创建可视化模型
      analyzeForSupport(objRef.current);
      
      setAnalysisComplete(true);
      setProcessingStatus("");
      setCurrentStep(2.5);
    }, 500);
  }, [analyzeOverhangs, createOverhangMarkers, analyzeForSupport]);

  const handlePointerMove = useCallback((event) => {
    if (currentStep !== 2.5 || !rendererRef.current) return;

    const rect = rendererRef.current.domElement.getBoundingClientRect();
    pointer.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(pointer.current, cameraRef.current);
    const intersects = raycaster.intersectObjects(overhangMarkersRef.current, false);

    if (intersects.length > 0) {
      const hovered = intersects[0].object;
      if (hovered !== hoveredMarker) {
        if (hoveredMarker) {
          const originalColor = hoveredMarker.userData.severity === 'high' ? 0xff0000 : 
                               hoveredMarker.userData.severity === 'medium' ? 0xff8800 : 0x00ff00;
          hoveredMarker.material.color.set(originalColor);
          hoveredMarker.scale.set(1, 1, 1);
        }
        
        hovered.material.color.set(0xffff00);
        hovered.scale.set(1.3, 1.3, 1.3);
        setHoveredMarker(hovered);
      }
    } else {
      if (hoveredMarker) {
        const originalColor = hoveredMarker.userData.severity === 'high' ? 0xff0000 : 
                             hoveredMarker.userData.severity === 'medium' ? 0xff8800 : 0x00ff00;
        hoveredMarker.material.color.set(originalColor);
        hoveredMarker.scale.set(1, 1, 1);
        setHoveredMarker(null);
      }
    }
  }, [currentStep, hoveredMarker]);

  const handleMarkerClick = useCallback((event) => {
    if (currentStep !== 2.5 || !rendererRef.current) return;

    const rect = rendererRef.current.domElement.getBoundingClientRect();
    pointer.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(pointer.current, cameraRef.current);
    const intersects = raycaster.intersectObjects(overhangMarkersRef.current, false);

    if (intersects.length > 0) {
      const selected = intersects[0].object;
      
      if (selected.userData.isCorrect) {
        setFeedbackMessage(
          `✅ Correct! This overhang at ${selected.userData.angle}° requires heavy support material and may cause print failure.`
        );
        setTimeout(() => setCurrentStep(3), 1500);
      } else {
        setFeedbackMessage(
          `⚠️ This area (${selected.userData.angle}°) needs some support, but try finding a more critical region (look for red markers).`
        );
      }
    }
  }, [currentStep]);

  const showAlternativeWorkflow = useCallback((workflowType) => {
    const workflows = {
      'laser-cutting': {
        id: 'FoundationalWorkflows_Laser cut stacked sheets (Algorithm 1)',
        title: '📚 Laser Cut Stacked Layers',
        keywords: ['Easy to Assemble', 'Wood'],
        explanation: 'No overhangs, no supports needed. Each layer is cut flat with no geometric constraints.'
      },
      'mold-making': {
        id: 'FoundationalWorkflows_Mold making',
        title: '🏺 Mold Making Solution',
        keywords: ['Silicone', 'Precision', 'Replication'],
        explanation: 'Cast the entire shape at once. No layer-by-layer constraints or overhang limitations.'
      },
      'wire-mesh': {
        id: 'FoundationalWorkflows_Wire mesh sculpture (Algorithm 1)',
        title: '🕸️ Wire Mesh Sculpture',
        keywords: ['Wire', 'Lightweight'],
        explanation: 'Create structural framework with no overhangs. Perfect for complex geometries.'
      }
    };

    const workflow = workflows[workflowType];
    if (workflow && driverObj) {
      driverObj.highlight({
        element: `[data-workflow="${workflow.id}"]`,
        popover: {
          side: "right",
          title: workflow.title,
          description: `${workflow.explanation}\n\nKey advantages: ${workflow.keywords.join(', ')}`,
          nextBtnText: "Got it!",
        }
      });

      workflow.keywords.forEach(keyword => {
        const chipElement = document.querySelector(`[data-keyword="${keyword}"]`);
        if (chipElement) {
          chipElement.classList.add('highlight-pulse');
          setTimeout(() => {
            chipElement.classList.remove('highlight-pulse');
          }, 4000);
        }
      });
    }
  }, [driverObj]);

  // 保留原始的loadModel函数
  const loadModel = useCallback(() => {
    setIsLoading(true);
    setError(null);

    if (objRef.current) {
      sceneRef.current?.remove(objRef.current);
      objRef.current.geometry?.dispose();
      if (Array.isArray(objRef.current.material)) {
        objRef.current.material.forEach((m) => m.dispose());
      } else {
        objRef.current.material?.dispose();
      }
      objRef.current = null;
    }

    clearOverhangMarkers();
    initHelpers();

    const loader = new STLLoader();
    const modelPath = "/models/twistT.stl";

    loader.load(
      modelPath,
      (geometry) => {
        const material = cachedMaterials.originalModelMaterial;
        const mesh = new THREE.Mesh(geometry, material);

        geometry.computeBoundingBox();
        const boundingBox = geometry.boundingBox;
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);

        geometry.translate(-center.x, -center.y, -center.z);
        geometry.rotateX(-Math.PI / 2);
        geometry.rotateY(-Math.PI / 2);

        mesh.updateMatrix();
        mesh.updateMatrixWorld(true);

        const objectBoundingBox = new THREE.Box3().setFromObject(mesh);
        mesh.position.y -= objectBoundingBox.min.y;
        mesh.updateMatrix();
        mesh.updateMatrixWorld(true);

        mesh.castShadow = true;
        mesh.receiveShadow = true;

        const size = new THREE.Vector3();
        objectBoundingBox.getSize(size);
        setModelDimensions({
          width: size.x,
          height: size.y,
          depth: size.z,
        });

        objRef.current = mesh;
        sceneRef.current?.add(mesh);

        setIsLoading(false);
        createHelpers();
      },
      (xhr) => {
        const percentComplete = (xhr.loaded / xhr.total) * 100;
        if (percentComplete < 100) {
          setProcessingStatus(`Loading model: ${Math.round(percentComplete)}%`);
        } else {
          setProcessingStatus("");
        }
      },
      (error) => {
        console.error(`Error loading STL:`, error);
        setError("Failed to load model");
        setIsLoading(false);
      }
    );
  }, [cachedMaterials, initHelpers, createHelpers, clearOverhangMarkers]);

  // 初始化场景
  useEffect(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );

    setTimeout(() => {
      loadModel();
    }, 0);

    return () => {
      if (sceneRef.current) {
        removeVisualHelpers(sceneRef.current);
      }

      // 清理draft angle图例
      const colorScale = document.getElementById("support-color-scale");
      if (colorScale) {
        document.body.removeChild(colorScale);
      }

      cleanup();
    };
  }, [loadModel, removeVisualHelpers]);

  // 事件监听
  useEffect(() => {
    const canvas = rendererRef.current?.domElement;
    if (!canvas) return;

    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerdown', handleMarkerClick);

    return () => {
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerdown', handleMarkerClick);
    };
  }, [handlePointerMove, handleMarkerClick]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container"></div>

      {/* Step 1 - 观察并开始切片 */}
      {!isLoading && currentStep === 1 && (
        <div className="instruction-box">
          <h3>🔍 Step 1: Slice the Model</h3>
          <p>
            Before we can identify support problems, we need to slice this model for 3D printing.
          </p>
          <p>
            <strong>Click "Slice Model" to generate the print preview.</strong>
          </p>
          <button 
            className="action-button" 
            onClick={sliceModel}
            disabled={isLoading || !!processingStatus}
          >
            Slice Model
          </button>
        </div>
      )}

      {/* Step 2 - 切片完成，开始分析 */}
      {!isLoading && currentStep === 2 && (
        <div className="instruction-box">
          <h3>🔬 Step 2: Analyze Support Requirements</h3>
          <p>
            Great! The model has been sliced. Now you can see the layer-by-layer structure.
            Look at areas where layers might not have support underneath.
          </p>
          <p>
            Support material is needed when overhangs exceed 45° from vertical.
            Let's analyze this model for problem areas.
          </p>
          <button 
            className="action-button" 
            onClick={analyzeAndShowMarkers}
            disabled={!!processingStatus}
          >
            Analyze Structure
          </button>
        </div>
      )}

      {/* Step 2.5 - 点击交互 */}
      {!isLoading && currentStep === 2.5 && (
        <div className="instruction-box">
          <h3>👆 Step 2: Click Problem Areas</h3>
          <p>
            <strong>Overhang Analysis Complete!</strong>
          </p>
          <p>
            • <span style={{color: '#ff0000'}}>●</span> blue markers: Critical overhangs requiring heavy support<br/>
          </p>
          <p>
            The colored model on the right shows <strong>draft angle analysis</strong> for 3D printing - 
            blue areas indicate steep overhangs that need support material.
          </p>
          <p>
            <strong>Click on a sphere marker to identify the most critical area.</strong>
          </p>
          {feedbackMessage && (
            <div className="feedback">{feedbackMessage}</div>
          )}
        </div>
      )}

      {/* Step 3 - 解释 */}
      {!isLoading && currentStep === 3 && (
        <div className="instruction-box">
          <h3>📘 Step 3: Understanding Support Problems</h3>
          <p>
            ✅ <strong>Excellent!</strong> You identified a critical overhang area.
          </p>
          <p>
            This region requires extensive support material, leading to:
          </p>
          <button className="action-button" onClick={() => setCurrentStep(4)}>
            How can I avoid this?
          </button>
        </div>
      )}

      {/* Step 4 - 替代方案 */}
      {!isLoading && currentStep === 4 && (
        <div className="instruction-box">
          <h3>💡 Step 4: Alternative Fabrication Methods</h3>
          <p>
            🔄 <strong>Consider different workflows</strong> that avoid overhang limitations:
          </p>
          
          <div style={{ margin: '15px 0' }}>
            <button 
              className="action-button alternative-button"
              onClick={() => showAlternativeWorkflow('laser-cutting')}
            >
              📚 Try Laser Cut Stacked Layers
              <div className="button-subtitle">
                No overhangs, flat sheet cutting only
              </div>
            </button>
            
            <button 
              className="action-button alternative-button"
              onClick={() => showAlternativeWorkflow('mold-making')}
            >
              🏺 Try Mold Making Approach
              <div className="button-subtitle">
                Cast entire shape, no layer constraints
              </div>
            </button>

            <button 
              className="action-button alternative-button"
              onClick={() => showAlternativeWorkflow('wire-mesh')}
            >
              🕸️ Try Wire Mesh Structure
              <div className="button-subtitle">
                Lightweight framework, no overhangs
              </div>
            </button>
          </div>

          <button className="action-button" onClick={() => setCurrentStep(5)}>
            I understand the alternatives
          </button>
        </div>
      )}

      {/* Step 5 - 完成 */}
      {!isLoading && currentStep === 5 && (
        <div className="instruction-box">
          <h3>🎉 Great Work!</h3>
          <p>
            <strong>You've successfully completed Example Task 2!</strong>
          </p>
          <div className="completion-summary">
            <h4>✅ What you learned:</h4>
            <ul>
              <li>Identifying <strong>support-heavy geometries</strong> in 3D printing</li>
              <li>Understanding <strong>overhang angle limitations</strong> (45° rule)</li>
              <li>When to consider <strong>alternative fabrication methods</strong></li>
            </ul>
          </div>
        </div>
      )}

      {/* 保留层控制 */}
      {previewSlices.length > 0 && currentStep >= 2 && (
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'white',
          padding: '10px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 10
        }}>
          <label style={{ fontSize: '12px', marginBottom: '5px', display: 'block' }}>
            Layer: {currentLayer + 1} / {previewSlices.length}
          </label>
          <input
            type="range"
            min="0"
            max={previewSlices.length - 1}
            value={currentLayer}
            onChange={(e) => showLayer(parseInt(e.target.value))}
            style={{ width: '150px' }}
          />
        </div>
      )}

      {/* 状态信息 */}
      {isLoading && <div className="loading-box">Loading model...</div>}
      
      {processingStatus && (
        <div className="loading-box">
          {processingStatus}
          {slicingProgress > 0 && slicingProgress < 100 && (
            <div style={{ marginTop: '4px' }}>
              <div style={{
                width: '200px',
                height: '4px',
                backgroundColor: '#e0e0e0',
                borderRadius: '2px',
                overflow: 'hidden',
                marginBottom: '2px'
              }}>
                <div style={{
                  width: `${slicingProgress}%`,
                  height: '100%',
                  backgroundColor: '#3288e6',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <span style={{ fontSize: '11px' }}>{slicingProgress}%</span>
            </div>
          )}
        </div>
      )}

      {error && <div className="error-box">Error: {error}</div>}
    </div>
  );
};

export default Example2SupportProblem;